import { useEffect, useMemo, useRef, useState } from 'react';
import { APIProvider, AdvancedMarker, InfoWindow, Map, useMap } from '@vis.gl/react-google-maps';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import type { LatLng } from '@charity-net/shared';
import { COORD_JITTER_METERS, jitterLocation } from '@charity-net/shared';

export type MapPin = {
  id: string;
  position: LatLng;
  title: string;
  thumbnailUrl?: string;
  jitter?: boolean;
  jitterSeed?: string;
  /** When true the pin is drawn purple (a wishlist match), otherwise red. */
  match?: boolean;
};

const MARKER_ICON = {
  match: 'https://maps.google.com/mapfiles/ms/icons/purple-dot.png',
  default: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
};

type MapViewProps = {
  /** Omit center/radius to show the whole continental US and fit to the pins. */
  center?: LatLng;
  radiusKm?: number;
  pins: MapPin[];
  renderPopup?: (id: string) => React.ReactNode;
};

const US_CENTER: LatLng = { lat: 39.8, lng: -98.6 };

export function MapView(props: MapViewProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_KEY;
  if (!apiKey) {
    return (
      <div className="grid place-items-center h-96 rounded-lg border bg-muted text-sm text-muted-foreground">
        Set VITE_GOOGLE_MAPS_KEY to enable the map.
      </div>
    );
  }
  return (
    <APIProvider apiKey={apiKey}>
      <div className="h-[60vh] w-full rounded-lg overflow-hidden border">
        <Map
          defaultCenter={props.center ?? US_CENTER}
          defaultZoom={props.center ? zoomForRadius(props.radiusKm ?? 10) : 4}
          mapId="charity-net"
          disableDefaultUI={false}
          gestureHandling="greedy"
        >
          <MarkerCluster pins={props.pins} renderPopup={props.renderPopup} />
          {!props.center && <FitToPins pins={props.pins} />}
        </Map>
      </div>
    </APIProvider>
  );
}

function MarkerCluster({
  pins,
  renderPopup,
}: {
  pins: MapPin[];
  renderPopup?: (id: string) => React.ReactNode;
}) {
  const map = useMap();
  const [openId, setOpenId] = useState<string | null>(null);
  const markersRef = useRef<google.maps.Marker[]>([]);
  const clustererRef = useRef<MarkerClusterer | null>(null);

  const displayed = useMemo(
    () =>
      pins.map((p) => ({
        ...p,
        position: p.jitter
          ? jitterLocation(p.position, COORD_JITTER_METERS, p.jitterSeed ?? p.id)
          : p.position,
      })),
    [pins],
  );

  useEffect(() => {
    if (!map) return;
    if (clustererRef.current) clustererRef.current.clearMarkers();
    markersRef.current = [];

    const markers = displayed.map((p) => {
      const marker = new google.maps.Marker({
        position: p.position,
        title: p.title,
        icon: p.match ? MARKER_ICON.match : MARKER_ICON.default,
      });
      marker.addListener('click', () => setOpenId(p.id));
      return marker;
    });
    markersRef.current = markers;
    clustererRef.current = new MarkerClusterer({ map, markers });
    return () => {
      clustererRef.current?.clearMarkers();
    };
  }, [map, displayed]);

  if (!renderPopup || !openId) return null;
  const pin = displayed.find((p) => p.id === openId);
  if (!pin) return null;
  return (
    <InfoWindow position={pin.position} onCloseClick={() => setOpenId(null)}>
      <div className="map-popup min-w-[200px]">{renderPopup(openId)}</div>
    </InfoWindow>
  );
}

/** Frames all pins whenever the set changes (e.g. switching map tabs). */
function FitToPins({ pins }: { pins: MapPin[] }) {
  const map = useMap();
  useEffect(() => {
    if (!map || pins.length === 0) return;
    if (pins.length === 1) {
      map.setCenter(pins[0]!.position);
      map.setZoom(12);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    pins.forEach((p) => bounds.extend(p.position));
    map.fitBounds(bounds, 48);
  }, [map, pins]);
  return null;
}

function zoomForRadius(radiusKm: number): number {
  if (radiusKm <= 2) return 14;
  if (radiusKm <= 5) return 13;
  if (radiusKm <= 10) return 12;
  if (radiusKm <= 25) return 11;
  return 10;
}
